/**
 * Training Modal Component
 * Modal for setting training completion dates (Fundamentals, Lighthouse, Adv Fundamentals)
 * Shows warnings for future dates and overwriting existing values
 */

import React, { useState, useEffect } from 'react';
import { Platform, View, Text, StyleSheet } from 'react-native';
import AdminModal from '../../components/AdminModal';
import AdminButton from '../../components/AdminButton';
import { InfoIcon, WarningIcon } from '../../components/AdminIcons';
import LhDatePicker from '../../../../components/LhDatePicker';
import { AdminColors } from '../../constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { useTheme } from '../../../../theme/useTheme';
import { TrainingType, TRAINING_LABELS } from './userManagementTypes';
import { isFutureDate, formatDateToString } from './userManagementUtils';

interface TrainingModalProps {
  visible: boolean;
  trainingType: TrainingType | null;
  selectedCount: number;
  overwriteCount: number;
  onClose: () => void;
  onApply: (date: string | null) => void;
}

const TrainingModal: React.FC<TrainingModalProps> = ({
  visible,
  trainingType,
  selectedCount,
  overwriteCount,
  onClose,
  onApply,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const trainingLabel = trainingType ? TRAINING_LABELS[trainingType] : '';

  useEffect(() => {
    if (visible) {
      setSelectedDate(null);
      setError(null);
    }
  }, [visible]);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    
    // Validate date
    if (date) {
      const dateString = formatDateToString(date);
      if (isFutureDate(dateString)) {
        setError('Training completion date cannot be in the future');
      } else {
        setError(null);
      }
    } else {
      setError(null);
    }
  };

  const handleApply = () => {
    if (selectedDate) {
      const dateString = formatDateToString(selectedDate);
      if (isFutureDate(dateString)) {
        setError('Training completion date cannot be in the future');
        return;
      }
      onApply(dateString);
    } else {
      onApply(null);
    }
    onClose();
  };

  const footer = (
    <>
      <AdminButton label="Cancel" variant="ghost" onPress={onClose} />
      <AdminButton 
        label="Apply" 
        variant="primary" 
        onPress={handleApply}
        disabled={!!error}
      />
    </>
  );

  return (
    <AdminModal
      visible={visible}
      title={`Set ${trainingLabel} Completion Date`}
      onClose={onClose}
      footer={footer}
      width={500}
    >
      <View style={styles.content}>
        {/* Info Banner */}
        <View style={[styles.infoBanner, isDark && { backgroundColor: colors.statusBackground.info, borderLeftColor: colors.brand.primary }]}>
          <InfoIcon color={isDark ? colors.brand.primary : undefined} />
          <Text style={[styles.infoText, isDark && { color: colors.brand.primary }]}>
            You are setting completion date for {selectedCount} selected user{selectedCount !== 1 ? 's' : ''}
          </Text>
        </View>

        {/* Date Picker */}
        <View style={styles.fieldGroup}>
          <Text style={[styles.label, isDark && { color: colors.text.primary }]}>Completion Date</Text>
          <LhDatePicker
            value={selectedDate}
            onChange={handleDateChange}
            placeholder="Select date"
            colorScheme="light"
            iconColor={AdminColors.blue700}
            disabledIconColor={AdminColors.gray200}
          />
          {error && <Text style={styles.errorText}>{error}</Text>}
        </View>

        {/* Overwrite Warning */}
        {overwriteCount > 0 && (
          <View style={[styles.warningBanner, isDark && { backgroundColor: colors.statusBackground.warning, borderLeftColor: colors.status.warning }]}>
            <WarningIcon color={isDark ? colors.status.warning : undefined} />
            <Text style={[styles.warningText, isDark && { color: colors.status.warning }]}>
              This will overwrite {overwriteCount} existing training date{overwriteCount !== 1 ? 's' : ''}
            </Text>
          </View>
        )}

        {/* Helper Text */}
        <Text style={[styles.helperText, isDark && { color: colors.text.secondary }]}>
          Note: Training completion dates cannot be in the future
        </Text>
      </View>
    </AdminModal>
  );
};

const styles = StyleSheet.create({
  content: {
    gap: 16,
  },
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: AdminColors.blue75,
    borderLeftWidth: 3,
    borderLeftColor: AdminColors.blue700,
    borderRadius: 5,
  },
  infoText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '500',
    color: '#1E5A7A',
    fontFamily: FontFamily.medium,
  },
  fieldGroup: {
    gap: 5,
  },
  label: {
    fontSize: 12,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
  },
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: AdminColors.yellow50,
    borderLeftWidth: 3,
    borderLeftColor: AdminColors.yellow200,
    borderRadius: 5,
  },
  warningText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '500',
    color: AdminColors.textWarning,
    fontFamily: FontFamily.medium,
  },
  helperText: {
    fontSize: 11,
    lineHeight: 14,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
  },
  errorText: {
    fontSize: 11,
    lineHeight: 14,
    color: AdminColors.textError,
    fontFamily: FontFamily.regular,
  },
});

export default TrainingModal;
